import { useTranslation } from "react-i18next";
import type { Provider } from "../../../../../../store/reseller-admin/providersStore";
import type { ThemeClasses } from "../types";
import ProviderGridCard from "./ProviderGridCard";
import ProviderListRow from "./ProviderListRow";

interface ProviderListProps {
  providers: Provider[];
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  selectedView: "grid" | "list";
  onDetails: (provider: Provider) => void;
  onEdit: (provider: Provider) => void;
  onDelete: (provider: Provider) => void;
  editingId?: string | null;
}

export default function ProviderList({
  providers,
  themeClasses,
  isDarkMode,
  selectedView,
  onDetails,
  onEdit,
  onDelete,
  editingId,
}: ProviderListProps) {
  const { t } = useTranslation("resellerAdmin");

  if (providers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <i className={`ri-cloud-line text-5xl ${themeClasses.textSecondary} mb-4`}></i>
        <h3 className={`text-lg font-semibold ${themeClasses.text} mb-2`}>
          {t("providers.noProviders")}
        </h3>
        <p className={`text-sm ${themeClasses.textSecondary}`}>
          {t("providers.noProvidersDescription")}
        </p>
      </div>
    );
  }

  if (selectedView === "grid") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {providers.map((provider) => (
          <ProviderGridCard
            key={provider.id}
            provider={provider}
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
            onDetails={onDetails}
            onEdit={onEdit}
            onDelete={onDelete}
            editLoading={editingId === provider.id}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {providers.map((provider) => (
        <ProviderListRow
          key={provider.id}
          provider={provider}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          onDetails={onDetails}
          onEdit={onEdit}
          onDelete={onDelete}
          editLoading={editingId === provider.id}
        />
      ))}
    </div>
  );
}
